import React, { useRef, useState } from "react";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable image uploader.
 * - Accepts drag-and-drop OR click-to-select
 * - POSTs to /api/upload (admin-only, httpOnly cookie auth)
 * - Calls onChange with the returned public URL string
 *
 * Props:
 *   value: string (current image URL)
 *   onChange: (url: string) => void
 *   testId?: string
 *   label?: string
 */
export const ImageUpload = ({ value = "", onChange, testId = "image-upload", label = "Görsel" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Sadece görsel dosyaları yüklenebilir");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Dosya 10MB'dan büyük olamaz");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data.url);
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => doUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files?.[0]);
  };

  const displayUrl = value?.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${value}` : value;

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <div className="text-xs uppercase tracking-widest font-bold text-ink-500">{label}</div>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed p-3 transition-colors cursor-pointer bg-white ${dragOver ? "border-brand-500 bg-brand-50" : "border-ink-100 hover:border-brand-300"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="sr-only"
          onChange={onPick}
          data-testid={`${testId}-input`}
        />
        {value ? (
          <div className="flex items-center gap-3">
            <img
              src={displayUrl}
              alt="Yüklenmiş görsel"
              className="h-20 w-20 rounded-xl object-cover ring-1 ring-ink-100 bg-ink-50"
              onError={(e) => { e.currentTarget.src = "/logo.png"; }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink-500 break-all">{value}</div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                  className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                  data-testid={`${testId}-replace`}
                >
                  Değiştir
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                  className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid={`${testId}-remove`}
                >
                  <X className="h-3 w-3" /> Kaldır
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-5 text-ink-500">
            {uploading ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                <div className="mt-2 text-sm font-semibold">Yükleniyor...</div>
              </>
            ) : (
              <>
                <div className="h-11 w-11 rounded-xl gradient-primary-soft grid place-items-center text-brand-600 mb-2">
                  <Upload className="h-5 w-5" />
                </div>
                <div className="text-sm font-semibold text-ink-700">Görseli sürükle-bırak veya seç</div>
                <div className="text-xs text-ink-500 mt-1">PNG, JPG, WebP · max 10MB</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
